import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag, MapPin, Truck, CreditCard, Banknote, Store, Tag, Loader2, User, Landmark, Send } from 'lucide-react';
import { useCart } from '../context/CartContext';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BACK2GAME_PRODUCT_ID = '3d42122a-175e-400e-9075-eae44afee648';
const BACK2GAME_DEAL_IMAGE = 'https://customer-assets.emergentagent.com/job_d56f6086-1f6c-43f9-a07d-5d56a502df97/artifacts/m1ozw06h_%2B%20%281%29.png';

const VALID_COUPONS = Object.fromEntries(
  Array.from({ length: 300 }, (_, i) => [String(i + 1), i + 1])
);

const Cart = () => {
  const { cart, removeFromCart, updateQuantity, getCartTotal, clearCart, freeShippingThreshold } = useCart();
  const navigate = useNavigate();
  const [paymentMethod, setPaymentMethod] = useState('credit_card');
  const [shippingMethod, setShippingMethod] = useState('pickup');
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupons, setAppliedCoupons] = useState([]);
  const [couponError, setCouponError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Personal details form
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_phone: '',
    street: '',
    house_number: '',
    city: '',
    notes: ''
  });
  
  // Pickup points state
  const [selectedPickupPoint, setSelectedPickupPoint] = useState(null);
  const [sdkLoaded, setSdkLoaded] = useState(false);
  const [pickupProvider, setPickupProvider] = useState(null); // 'ups' or 'hfd'
  // "נקודת איסוף HFD" list-picker (added Jul 2026, mirrors QuickOrder):
  // search-by-city list instead of the map. Maps to shipping_method
  // 'pickup' on submit so all backend/Admin logic stays unchanged.
  const [hfdAllPoints, setHfdAllPoints] = useState([]);
  const [hfdListSearch, setHfdListSearch] = useState('');
  const [hfdListPoint, setHfdListPoint] = useState(null);
  const [hfdListLoading, setHfdListLoading] = useState(false);

  // Always-current copy of formData accessible from inside long-lived
  // callbacks (the PickUP SDK 'pickups-before-open' listener captures
  // formData in its closure once and would otherwise see stale values).
  const formDataRef = useRef(formData);
  useEffect(() => { formDataRef.current = formData; }, [formData]);

  // Load PickUP SDK
  useEffect(() => {
    if (document.getElementById('pickup-sdk')) { setSdkLoaded(true); return; }
    const script = document.createElement('script');
    script.id = 'pickup-sdk';
    script.src = 'https://pickuppoint.co.il/api/ups-pickups.sdk.all.js';
    script.async = true;
    script.onload = () => setSdkLoaded(true);
    document.head.appendChild(script);
  }, []);

  // Listen for PickUP SDK selection event
  const handlePickupChosen = useCallback((e) => {
    const data = e.detail || e.originalEvent?.detail;
    if (data) {
      console.log('PICKUP SDK RAW DATA:', JSON.stringify(data));
      const name = data.title || data.name || data.Name || '';
      const street = data.street || data.address || data.Address || '';
      const city = data.city || data.City || '';
      const pointName = name + (city ? `, ${city}` : '');
      setPickupProvider('ups');
      setSelectedPickupPoint({
        PointName: pointName,
        Address: street + (city ? `, ${city}` : ''),
        id: data.iid || data.id || data.Id || '',
      });
      setFormData(prev => ({ ...prev, customer_address: street + (city ? `, ${city}` : '') }));
    }
  }, []);

  useEffect(() => {
    document.body.addEventListener('pickups-after-choosen', handlePickupChosen);
    return () => document.body.removeEventListener('pickups-after-choosen', handlePickupChosen);
  }, [handlePickupChosen]);
  
  // When cash is selected, force home delivery
  useEffect(() => {
    if (paymentMethod === 'cash') {
      setShippingMethod('delivery');
    }
  }, [paymentMethod]);


  // Global function for HFD point selection by index
  useEffect(() => {
    window.__selectHfdPoint = (idx) => {
      const pts = window.__hfdPointsData;
      if (!pts || !pts[idx]) return;
      const pt = pts[idx];
      setPickupProvider('hfd');
      const ptAddr = [pt.street, pt.house].filter(Boolean).join(' ');
      setSelectedPickupPoint({
        PointName: `${pt.spotName}, ${pt.city}`,
        Address: ptAddr ? `${ptAddr}, ${pt.city}` : `${pt.city}`,
        hfdSpotId: String(pt.spotId),
      });
      setFormData(prev => ({ ...prev, customer_address: ptAddr ? `${ptAddr}, ${pt.city}` : pt.city }));
      if (window.PickupsSDK) window.PickupsSDK.closeModal();
    };
    return () => { delete window.__selectHfdPoint; delete window.__hfdPointsData; };
  }, []);

  // Pre-fetch ALL HFD pickup points in Israel (one cached call). The backend
  // tags each point with `kind: 'store' | 'locker'` so we can render unified
  // icons regardless of provider.
  useEffect(() => {
    if (!sdkLoaded || window.__hfdPointsData) return;
    axios.post(`${API}/hfd/pickup-points-all`)
      .then(r => { if (r.data?.length) window.__hfdPointsData = r.data; })
      .catch(() => {});
  }, [sdkLoaded]);

  // Hide every native UPS marker that the PickUP SDK tries to add to the
  // map. We patch `google.maps.Marker` once: any marker created WITHOUT
  // the `__bh` flag is force-hidden immediately. Our HFD markers (added
  // inside `addMarkersToMap` below) pass `__bh: true` and stay visible.
  // Done May 2026 — we stopped working with UPS, customers should only
  // see HFD pickup points.
  useEffect(() => {
    if (!sdkLoaded) return;
    if (window.__bh_marker_patched) return;
    if (!window.google?.maps?.Marker) return;
    window.__bh_marker_patched = true;
    const OG = window.google.maps.Marker;
    const Patched = function (opts) {
      const o = opts || {};
      const inst = new OG(o);
      if (!o.__bh) {
        // Not one of ours — strip it from the map. Done in setTimeout so
        // the SDK's own bookkeeping (e.g. setting position) still happens
        // first; the marker is invisible regardless.
        try { inst.setVisible(false); } catch (_e) { /* ignore */ }
        setTimeout(() => { try { inst.setMap(null); } catch (_e) { /* ignore */ } }, 50);
      }
      return inst;
    };
    Patched.prototype = OG.prototype;
    Object.setPrototypeOf(Patched, OG);
    window.google.maps.Marker = Patched;
  }, [sdkLoaded]);

  // Unified pickup-point modal: single visual language for stores vs lockers
  // regardless of the underlying provider (UPS / HFD). The customer doesn't
  // care if a point is HFD or UPS - they care whether they can walk in
  // (חנות) or open a self-service locker (לוקר).
  useEffect(() => {
    if (!sdkLoaded) return;
    let keepAliveInterval = null;
    let hfdMarkerRefs = [];

    // Two unified pin-icons (matching standard Material map markers).
    // Blue = walk-in store, Orange = self-service locker.
    const STORE_ICON_URL = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="32" height="42" viewBox="0 0 32 42"><path d="M16 0C7.2 0 0 7.2 0 16c0 12 16 26 16 26s16-14 16-26C32 7.2 24.8 0 16 0z" fill="#2563EB"/><circle cx="16" cy="15" r="9" fill="white"/><path d="M11 11h10v2H11zm0 3h10v6H11z" fill="#2563EB"/></svg>');
    const LOCKER_ICON_URL = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="32" height="42" viewBox="0 0 32 42"><path d="M16 0C7.2 0 0 7.2 0 16c0 12 16 26 16 26s16-14 16-26C32 7.2 24.8 0 16 0z" fill="#F97316"/><rect x="9" y="8" width="14" height="16" rx="1.5" fill="white"/><path d="M11 11h4v3h-4zm6 0h4v3h-4zm-6 5h4v3h-4zm6 0h4v3h-4z" fill="#F97316"/></svg>');

    const addMarkersToMap = (pts, map) => {
      hfdMarkerRefs.forEach(m => m.setMap(null));
      hfdMarkerRefs = [];
      pts.forEach((pt, idx) => {
        if (!pt.latitude || !pt.longitude) return;
        const isLocker = pt.kind === 'locker';
        const iconUrl = isLocker ? LOCKER_ICON_URL : STORE_ICON_URL;
        const marker = new window.google.maps.Marker({
          __bh: true, // flag to keep this marker visible — our HFD point
          position: { lat: parseFloat(pt.latitude), lng: parseFloat(pt.longitude) },
          map,
          title: pt.spotName,
          icon: { url: iconUrl, scaledSize: new window.google.maps.Size(32, 42), anchor: new window.google.maps.Point(16, 42) },
          zIndex: 9999,
        });
        const safeName = (pt.spotName || '').replace(/"/g, '&quot;').replace(/</g, '&lt;');
        const safeAddr = (`${[pt.street, pt.house].filter(Boolean).join(' ')}${pt.street ? ', ' : ''}${pt.city || ''}`).replace(/"/g, '&quot;').replace(/</g, '&lt;');
        const safeRemarks = (pt.spotRemarks || '').replace(/"/g, '&quot;').replace(/</g, '&lt;');
        const tagBg = isLocker ? '#F97316' : '#2563EB';
        const tagText = isLocker ? 'לוקר' : 'נקודת איסוף';
        const infoContent = `<div style="text-align:right;direction:rtl;font-family:Heebo,sans-serif;min-width:200px;padding:4px"><div style="display:flex;align-items:center;gap:6px;margin-bottom:6px"><span style="background:${tagBg};color:white;padding:2px 8px;border-radius:4px;font-size:10px;font-weight:bold">${tagText}</span><strong style="font-size:13px">${safeName}</strong></div><p style="font-size:11px;color:#555;margin:2px 0">${safeAddr}</p><p style="font-size:10px;color:#888;margin:2px 0">${safeRemarks}</p><button onclick="window.__selectHfdPoint(${idx})" style="margin-top:8px;background:${tagBg};color:white;border:none;padding:8px 16px;border-radius:8px;cursor:pointer;font-weight:bold;font-size:13px;width:100%">בחר נקודה זו</button></div>`;
        const iw = new window.google.maps.InfoWindow({ content: infoContent });
        marker.addListener('click', () => { iw.open(map, marker); });
        hfdMarkerRefs.push(marker);
      });
    };

    const ensureMarkersVisible = () => {
      const sdk = window.PickupsSDK;
      if (!sdk || !sdk._mapCanvas) return;
      const map = sdk._mapCanvas;
      const pts = window.__hfdPointsData;
      if (!pts || pts.length === 0) return;
      if (hfdMarkerRefs.length > 0 && hfdMarkerRefs[0].getMap()) return;
      addMarkersToMap(pts, map);
    };

    const injectSidebar = () => {
      const sidebar = document.querySelector('.ct-googleMap--results');
      const pts = window.__hfdPointsData;
      if (!sidebar) return;
      // Remove old HFD items + old legend on every redraw so we don't dupe.
      sidebar.querySelectorAll('.hfd-sidebar-item, .hfd-legend, .ups-badge').forEach(el => el.remove());
      if (!pts || pts.length === 0) return;
      const sdk = window.PickupsSDK;
      const map = sdk?._mapCanvas;
      const bounds = map?.getBounds();
      let visiblePts = pts;
      if (bounds) {
        visiblePts = pts.filter(pt => pt.latitude && pt.longitude && bounds.contains({ lat: parseFloat(pt.latitude), lng: parseFloat(pt.longitude) }));
      }
      visiblePts.slice(0, 25).forEach((pt) => {
        const idx = pts.indexOf(pt);
        const isLocker = pt.kind === 'locker';
        const stripeColor = isLocker ? '#F97316' : '#2563EB';
        const tagBg = stripeColor;
        const tagText = isLocker ? 'לוקר' : 'נקודת איסוף';
        const li = document.createElement('div');
        li.className = 'ct-googleMap--sidebarItem hfd-sidebar-item';
        li.style.cssText = `border-right:3px solid ${stripeColor};cursor:pointer;`;
        const safeAddr = (`${[pt.street, pt.house].filter(Boolean).join(' ')}${pt.street ? ', ' : ''}${pt.city || ''}`).replace(/"/g, '&quot;').replace(/</g, '&lt;');
        li.innerHTML = `<div style="padding:6px 8px"><div style="display:flex;align-items:center;gap:4px;margin-bottom:2px"><span style="background:${tagBg};color:white;padding:1px 5px;border-radius:3px;font-size:8px;font-weight:bold">${tagText}</span><span style="font-size:12px;font-weight:bold;color:#333">${(pt.spotName || '').replace(/</g, '&lt;')}</span></div><span style="font-size:10px;color:#666">${safeAddr}</span></div>`;
        li.addEventListener('click', () => { window.__selectHfdPoint(idx); });
        sidebar.appendChild(li);
      });
    };

    const injectLegend = () => {
      const sidebar = document.querySelector('.ct-googleMap--results');
      if (!sidebar) return;
      sidebar.querySelectorAll('.hfd-legend').forEach(el => el.remove());
      const legend = document.createElement('div');
      legend.className = 'hfd-legend';
      legend.style.cssText = 'padding:8px 10px;background:#f8f9fa;border-bottom:1px solid #e0e0e0;display:flex;align-items:center;gap:10px;direction:rtl;position:sticky;top:0;z-index:10;flex-wrap:wrap;';
      legend.innerHTML = `
        <span style="display:inline-flex;align-items:center;gap:4px;font-size:10px;color:#666">
          <span style="width:10px;height:10px;background:#2563EB;border-radius:50%;display:inline-block"></span>
          נקודת איסוף בחנות
        </span>
        <span style="display:inline-flex;align-items:center;gap:4px;font-size:10px;color:#666">
          <span style="width:10px;height:10px;background:#F97316;border-radius:50%;display:inline-block"></span>
          לוקר עצמאי
        </span>`;
      sidebar.prepend(legend);
    };

    // Hide the UPS branding text/badge from the SDK's own UI - the customer
    // only cares about icon (store vs locker), not provider.
    const hideUpsBranding = () => {
      // Inject CSS once
      if (!document.getElementById('unified-pickup-css')) {
        const css = document.createElement('style');
        css.id = 'unified-pickup-css';
        css.textContent = `
          /* Hide PickUP/UPS logo+watermark inside the modal */
          .pickups-modal img[alt*="UPS" i],
          .pickups-modal img[src*="ups" i],
          .pickups-modal img[src*="pickup" i],
          .pickups-modal img[alt*="PickUP" i],
          .ct-googleMap img[alt*="UPS" i],
          .ct-googleMap img[src*="ups" i],
          .ct-googleMap img[src*="pickup" i] { display:none !important; }
          /* The PickUP "PickUP Access Point" wordmark in the top-left */
          .pickups-modal [class*="logo" i],
          .pickups-modal [class*="brand" i] { display:none !important; }
          /* Native UPS sidebar items get hidden — only our injected
             .hfd-sidebar-item items stay. */
          .ct-googleMap--sidebarItem:not(.hfd-sidebar-item) { display:none !important; }
        `;
        document.head.appendChild(css);
      }
      // Walk DOM and remove any text node that says UPS/PickUP
      const modal = document.querySelector('.pickups-modal') || document.querySelector('.ct-googleMap');
      if (!modal) return;
      modal.querySelectorAll('*').forEach(el => {
        const t = (el.textContent || '').trim();
        if (t === 'UPS' || t === 'PickUP' || t === 'PickUP Access Point' || t === 'Powered by UPS') {
          el.style.display = 'none';
        }
      });
    };

    const showOverlay = () => {
      const modal = document.querySelector('.pickups-modal') || document.querySelector('.ct-googleMap');
      if (!modal) return;
      let overlay = document.getElementById('hfd-loading-overlay');
      if (overlay) return;
      overlay = document.createElement('div');
      overlay.id = 'hfd-loading-overlay';
      overlay.style.cssText = 'position:fixed;top:0;left:0;width:100vw;height:100vh;background:rgba(255,255,255,0.6);backdrop-filter:blur(3px);z-index:999999;display:flex;align-items:center;justify-content:center;direction:rtl;';
      overlay.innerHTML = `<div style="background:white;padding:24px 48px;border-radius:16px;box-shadow:0 4px 24px rgba(0,0,0,0.15);text-align:center"><div style="width:40px;height:40px;border:3px solid #e0e0e0;border-top:3px solid #2563EB;border-radius:50%;animation:spin 0.8s linear infinite;margin:0 auto 14px"></div><p style="font-size:16px;font-weight:bold;color:#333;font-family:Heebo,sans-serif;margin:0">כמה רגעים...</p><p style="font-size:13px;color:#888;font-family:Heebo,sans-serif;margin:6px 0 0">טוענים את כל נקודות האיסוף</p></div>`;
      document.body.appendChild(overlay);
      if (!document.getElementById('hfd-spin-style')) {
        const style = document.createElement('style');
        style.id = 'hfd-spin-style';
        style.textContent = '@keyframes spin { to { transform: rotate(360deg) } }';
        document.head.appendChild(style);
      }
    };
    const hideOverlay = () => {
      const overlay = document.getElementById('hfd-loading-overlay');
      if (overlay) overlay.remove();
    };

    const onOpen = async () => {
      if (keepAliveInterval) clearInterval(keepAliveInterval);
      keepAliveInterval = setInterval(() => {
        ensureMarkersVisible();
        injectSidebar();
        injectLegend();
        hideUpsBranding();
      }, 3000);

      // Recenter the map on the customer's typed-in address so they don't
      // see the entire country. Uses the public Google Maps Geocoder
      // (already loaded by the PickUP SDK) — no extra script needed. We
      // run this 1.5s after open to be sure the map canvas has mounted.
      const recenterToCustomer = () => {
        try {
          const sdk = window.PickupsSDK;
          if (!sdk?._mapCanvas) return;
          const cur = formDataRef.current || {};
          const street = (cur.street || '').trim();
          const houseNum = (cur.house_number || '').trim();
          const city = (cur.city || '').trim();
          const parts = [];
          if (street) parts.push(street + (houseNum ? ` ${houseNum}` : ''));
          if (city) parts.push(city);
          if (!parts.length) return;
          const query = parts.join(', ') + ', Israel';
          if (!window.google?.maps?.Geocoder) return;
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ address: query, region: 'il' }, (results, status) => {
            if (status === 'OK' && results && results[0]) {
              const loc = results[0].geometry.location;
              sdk._mapCanvas.setCenter(loc);
              sdk._mapCanvas.setZoom(14); // city-level zoom
            }
          });
        } catch (_e) { /* graceful no-op */ }
      };

      const pts = window.__hfdPointsData;
      if (pts && pts.length > 0) {
        setTimeout(() => {
          const sdk = window.PickupsSDK;
          if (sdk?._mapCanvas) {
            addMarkersToMap(pts, sdk._mapCanvas);
            injectSidebar();
            injectLegend();
            hideUpsBranding();
            recenterToCustomer();
          }
        }, 1500);
        return;
      }

      setTimeout(showOverlay, 500);
      setTimeout(async () => {
        const sdk = window.PickupsSDK;
        if (!sdk || !sdk._mapCanvas) { hideOverlay(); return; }
        recenterToCustomer();
        let waited = 0;
        const poll = setInterval(() => {
          waited += 500;
          const data = window.__hfdPointsData;
          if (data && data.length > 0) {
            clearInterval(poll);
            addMarkersToMap(data, sdk._mapCanvas);
            setTimeout(() => { injectSidebar(); injectLegend(); hideUpsBranding(); recenterToCustomer(); }, 500);
            hideOverlay();
          } else if (waited > 25000) {
            clearInterval(poll);
            hideOverlay();
          }
        }, 500);
      }, 1500);
    };

    document.body.addEventListener('pickups-before-open', onOpen);
    return () => {
      document.body.removeEventListener('pickups-before-open', onOpen);
      if (keepAliveInterval) clearInterval(keepAliveInterval);
      hfdMarkerRefs.forEach(m => m.setMap(null));
    };
  }, [sdkLoaded]);

  
  // Shipping price tiers — kept aligned with QuickOrder.js:
  //   pickup        = 17 ₪ (HFD national network)
  //   pickup_zigzag = 17 ₪ (eHouse auto-routes to closest Zigzag locker)
  //   self_pickup   = 0  ₪
  //   delivery      = 29 ₪ (home delivery, lowered from 37 per user spec)
  // Free-shipping rule (per user spec, June 2026):
  //   subtotal ≥ 380 ₪  →  pickup tiers become FREE.
  //   home delivery still charged.
  const baseShippingCost = (shippingMethod === 'pickup' || shippingMethod === 'pickup_hfd') ? 17 : shippingMethod === 'pickup_zigzag' ? 17 : shippingMethod === 'self_pickup' ? 0 : 29;
  const effectiveThreshold = freeShippingThreshold || 450;
  const cartSub = getCartTotal();
  // "Pickup-free" kicks in at 380 ₪ irrespective of the global threshold.
  const pickupFree = (shippingMethod === 'pickup' || shippingMethod === 'pickup_hfd' || shippingMethod === 'pickup_zigzag') && cartSub >= 380;
  const isFreeShipping = pickupFree || (effectiveThreshold > 0 && cartSub >= effectiveThreshold && shippingMethod !== 'self_pickup');
  const shippingCost = isFreeShipping ? 0 : baseShippingCost;
  const discount = appliedCoupons.reduce((sum, code) => sum + (VALID_COUPONS[code] || 0), 0);
  const subtotalWithShipping = getCartTotal() + shippingCost;
  const totalWithShipping = Math.max(0, subtotalWithShipping - discount);

  const handleApplyCoupon = () => {
    const code = couponCode.trim();
    if (VALID_COUPONS[code] === undefined) {
      setCouponError('קוד קופון לא תקין');
      return;
    }
    if (appliedCoupons.includes(code)) {
      setCouponError('קופון זה כבר הופעל');
      return;
    }
    setAppliedCoupons(prev => [...prev, code]);
    setCouponCode('');
    setCouponError('');
  };

  const handleRemoveCoupon = (code) => {
    setAppliedCoupons(prev => prev.filter(c => c !== code));
  };

  // Lazy-load the HFD points list the first time the list-picker option is
  // chosen. Reuses the map's prefetched window.__hfdPointsData when present.
  useEffect(() => {
    if (shippingMethod !== 'pickup_hfd' || hfdAllPoints.length > 0 || hfdListLoading) return;
    if (window.__hfdPointsData?.length) { setHfdAllPoints(window.__hfdPointsData); return; }
    setHfdListLoading(true);
    axios.post(`${API}/hfd/pickup-points-all`)
      .then(r => setHfdAllPoints(r.data || []))
      .catch(() => toast.error('שגיאה בטעינת נקודות איסוף'))
      .finally(() => setHfdListLoading(false));
  }, [shippingMethod, hfdAllPoints.length, hfdListLoading]);

  const handlePickupSelect = () => {
    setShippingMethod('pickup');
    setPickupProvider('ups'); // default, will change if HFD is selected
    if (window.PickupsSDK) {
      window.PickupsSDK.onClick();
    }
    // Auto-zoom map to the address the customer already typed in the form
    // (city + street). Saves them re-typing and lands them right on the
    // points relevant to their delivery area. The PickUP SDK doesn't
    // expose a public "set query" method, so we drive its built-in
    // search input and trigger Enter once the modal renders.
    const tryPrefillSearch = () => {
      const parts = [];
      const street = (formData.street || '').trim();
      const houseNum = (formData.house_number || '').trim();
      const city = (formData.city || '').trim();
      if (street) parts.push(street + (houseNum ? ` ${houseNum}` : ''));
      if (city) parts.push(city);
      const query = parts.join(', ');
      if (!query) return;
      // Wait up to 3s for the modal's search input to mount, then fill it
      // and dispatch 'input' + 'Enter' so the SDK fetches points for that
      // address. We try every 150ms.
      let elapsed = 0;
      const poll = setInterval(() => {
        elapsed += 150;
        const modal = document.querySelector('.pickups-modal') || document.querySelector('.ct-googleMap');
        if (!modal) {
          if (elapsed > 3000) clearInterval(poll);
          return;
        }
        // PickUP SDK uses a generic <input type=text> in its top toolbar
        // for address search.
        const input = modal.querySelector('input[type="text"], input[type="search"]');
        if (!input) {
          if (elapsed > 3000) clearInterval(poll);
          return;
        }
        clearInterval(poll);
        // Set value via the React-friendly native setter so onChange fires.
        const nativeSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
        nativeSetter.call(input, query);
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.focus();
        // Submit by simulating Enter — covers SDKs that listen for keydown
        // OR for an embedded search button click.
        input.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', code: 'Enter', keyCode: 13, which: 13, bubbles: true }));
        const goBtn = modal.querySelector('button[type="submit"], .search-btn, [aria-label*="search" i]');
        if (goBtn) goBtn.click();
      }, 150);
    };
    setTimeout(tryPrefillSearch, 500);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_name.trim() || !formData.customer_phone.trim()) {
      toast.error('נא למלא שם וטלפון');
      return;
    }
    
    if ((shippingMethod === 'delivery' || shippingMethod === 'pickup_zigzag') && (!formData.street.trim() || !formData.city.trim())) {
      toast.error('נא למלא כתובת מלאה (זיגזג יבחר את נקודת האיסוף הקרובה אליכם אוטומטית)');
      return;
    }
    
    if (shippingMethod === 'pickup' && !selectedPickupPoint) {
      toast.error('נא לבחור נקודת איסוף');
      return;
    }

    if (shippingMethod === 'pickup_hfd' && !hfdListPoint) {
      toast.error('נא לבחור נקודת איסוף HFD');
      return;
    }
    
    setLoading(true);
    
    try {
      // For Zigzag pickup the customer fills their HOME address. eHouse
      // auto-routes the package to the closest Zigzag locker on their
      // side. So we keep `customer_address` as the customer's address —
      // the admin sends it as-is when creating the eHouse Zigzag shipment.
      const isHfdList = shippingMethod === 'pickup_hfd';
      const fullAddress = shippingMethod === 'pickup'
        ? (selectedPickupPoint?.Address || '')
        : isHfdList
          ? `${[hfdListPoint?.street, hfdListPoint?.house].filter(Boolean).join(' ')}, ${hfdListPoint?.city || ''}`.replace(/^, /, '')
          : `${formData.street} ${formData.house_number}, ${formData.city}`;
      
      const orderData = {
        ...formData,
        customer_address: fullAddress,
        pickup_point_name: shippingMethod === 'pickup' ? (selectedPickupPoint?.PointName || '') : isHfdList ? `${hfdListPoint.spotName}, ${hfdListPoint.city}` : '',
        pickup_point_id: shippingMethod === 'pickup' ? (selectedPickupPoint?.id || selectedPickupPoint?.hfdSpotId || '') : isHfdList ? String(hfdListPoint.spotId) : '',
        pickup_provider: shippingMethod === 'pickup' ? (pickupProvider || 'ups') : isHfdList ? 'hfd' : '',
        hfd_spot_id: isHfdList ? String(hfdListPoint.spotId) : (selectedPickupPoint?.hfdSpotId || ''),
        payment_method: paymentMethod,
        shipping_method: isHfdList ? 'pickup' : shippingMethod,
        items: cart.map(item => ({
          product_id: item.id,
          product_name: item.name,
          quantity: item.quantity,
          price: item.price
        })),
        shipping_cost: shippingCost,
        total_amount: totalWithShipping,
        coupons: appliedCoupons.length > 0 ? appliedCoupons.join(',') : '',
        discount_total: discount
      };
      
      const orderResponse = await axios.post(`${API}/orders`, orderData);
      const orderId = orderResponse.data.id;
      
      if (paymentMethod === 'credit_card') {
        const currentUrl = window.location.origin;
        const paymentResponse = await axios.post(`${API}/payments/cardcom/create`, {
          order_id: orderId,
          amount: totalWithShipping,
          customer_name: formData.customer_name,
          customer_email: '',
          customer_phone: formData.customer_phone,
          customer_address: fullAddress,
          product_name: `הזמנה #${orderId.substring(0, 8)}`,
          success_url: `${currentUrl}/checkout?status=success`,
          failure_url: `${currentUrl}/checkout?status=failed`,
          webhook_url: `${BACKEND_URL}/api/payments/cardcom/webhook`
        });
        
        if (paymentResponse.data.success) {
          navigate('/checkout', { 
            state: { 
              paymentUrl: paymentResponse.data.payment_url,
              total: totalWithShipping,
              orderId: orderId
            }
          });
        } else {
          toast.error('שגיאה ביצירת עמוד התשלום');
        }
      } else if (paymentMethod === 'bit') {
        // Redirect directly to Bit payment page (uses CardCom LowProfile BitUrl)
        clearCart();
        navigate(`/pay/bit?amount=${totalWithShipping}&order=${orderResponse.data.order_number || orderId.substring(0,8)}&name=${encodeURIComponent(formData.customer_name)}`);
      } else if (paymentMethod === 'paypal') {
        clearCart();
        navigate(`/pay/paypal?amount=${totalWithShipping}&order=${orderResponse.data.order_number || orderId.substring(0,8)}&name=${encodeURIComponent(formData.customer_name)}`);
      } else if (paymentMethod === 'paybox') {
        clearCart();
        navigate(`/pay/paybox?amount=${totalWithShipping}&order=${orderResponse.data.order_number || orderId.substring(0,8)}&name=${encodeURIComponent(formData.customer_name)}`);
      } else if (paymentMethod === 'bank_transfer') {
        navigate('/checkout', { 
          state: { 
            bankTransfer: true, 
            total: totalWithShipping,
            orderId: orderId 
          }
        });
      } else {
        clearCart();
        toast.success('ההזמנה בוצעה בהצלחה!');
        navigate('/checkout', { state: { orderPlaced: true } });
      }
    } catch (error) {
      console.error('Error placing order:', error);
      const errMsg = error.response?.data?.detail;
      const errStr = typeof errMsg === 'string' ? errMsg : (Array.isArray(errMsg) ? errMsg.map(e => e.msg || e).join(', ') : 'שגיאה בשליחת ההזמנה');
      toast.error(errStr);
    } finally {
      setLoading(false);
    }
  };
  
  if (cart.length === 0) {
    return (
      <div className="container py-16" data-testid="empty-cart">
        <div className="text-center py-16">
          <ShoppingBag className="w-24 h-24 text-stone-300 mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-[#2C3E2D] mb-4" style={{ fontFamily: 'Heebo, sans-serif' }}>
            העגלה שלך ריקה
          </h2>
          <p className="text-stone-600 mb-8">
            העגלה שלך ריקה. הוסף מוצרים כדי להמשיך
          </p>
          <Link
            to="/products"
            data-testid="continue-shopping-button"
            className="inline-flex h-12 px-8 rounded-full bg-[#D97706] text-white font-bold hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 items-center"
            style={{ fontFamily: 'Heebo, sans-serif' }}
          >
            המשך לקניות
          </Link>
        </div>
      </div>
    );
  }
  
  return (
    <div className="container py-16" data-testid="cart-page">
      <h1 className="text-4xl md:text-5xl font-bold text-[#2C3E2D] mb-12 text-center" style={{ fontFamily: 'Heebo, sans-serif' }} data-testid="cart-title">
        עגלת הקניות שלך
      </h1>
      
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column: Cart Items + Personal Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Cart Items */}
            <div className="space-y-4" data-testid="cart-items">
              {cart.map((item) => (
                <div
                  key={item.id}
                  data-testid={`cart-item-${item.id}`}
                  className="bg-white rounded-2xl p-4 md:p-6 border border-stone-100 hover:shadow-md transition-shadow overflow-hidden"
                >
                  <div className="flex gap-4 md:gap-6">
                    <img
                      src={item.id === BACK2GAME_PRODUCT_ID ? BACK2GAME_DEAL_IMAGE : item.image}
                      alt={item.name}
                      className="w-20 h-20 md:w-24 md:h-24 object-cover rounded-lg flex-shrink-0"
                    />
                    
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg md:text-xl font-semibold text-[#2C3E2D] mb-1 truncate" style={{ fontFamily: 'Heebo, sans-serif' }}>
                        {item.name}
                      </h3>
                      {item.sideCartDiscount && (
                        <p className="text-green-600 font-bold text-sm mb-0.5" style={{ fontFamily: 'Heebo, sans-serif' }}>
                          מוצר ב10% הנחה
                        </p>
                      )}
                      {item.id === BACK2GAME_PRODUCT_ID && (
                        <p className="text-[#D97706] font-bold text-sm mb-1" style={{ fontFamily: 'Heebo, sans-serif' }}>
                          2 במחיר של 1
                        </p>
                      )}
                      <p className="text-stone-600 text-sm mb-3 line-clamp-2 overflow-hidden">{item.description}</p>
                      
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            data-testid={`decrease-quantity-${item.id}`}
                            className="w-8 h-8 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center transition-colors"
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          
                          <span className="text-lg font-semibold w-8 text-center" data-testid={`quantity-${item.id}`}>
                            {item.quantity}
                          </span>
                          
                          <button
                            type="button"
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            data-testid={`increase-quantity-${item.id}`}
                            className="w-8 h-8 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center transition-colors"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          {item.originalPrice && item.originalPrice !== item.price && (
                            <span className="text-base text-stone-400 line-through">₪{(item.originalPrice * item.quantity).toFixed(0)}</span>
                          )}
                          <span className="text-xl md:text-2xl font-bold text-[#D97706]" style={{ fontFamily: 'Heebo, sans-serif' }}>
                            ₪{(item.price * item.quantity).toFixed(2)}
                          </span>
                          
                          <button
                            type="button"
                            onClick={() => removeFromCart(item.id)}
                            data-testid={`remove-item-${item.id}`}
                            className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            aria-label="הסר מהעגלה"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Mobile Coupon Section - visible only on mobile */}
            <div className="lg:hidden bg-white rounded-2xl p-5 border-2 border-stone-200 shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <Tag className="w-4 h-4 text-[#D97706]" />
                <span className="font-semibold text-[#2C3E2D] text-sm" style={{ fontFamily: 'Heebo, sans-serif' }}>קוד קופון</span>
              </div>
              {appliedCoupons.length > 0 && (
                <div className="space-y-2 mb-3">
                  {appliedCoupons.map((code) => (
                    <div key={code} className="flex items-center justify-between bg-green-50 border border-green-200 rounded-xl p-2.5">
                      <div className="flex items-center gap-2">
                        <span className="text-green-700 font-bold text-sm">קופון {code}</span>
                        <span className="text-green-600 text-sm">(-₪{VALID_COUPONS[code]})</span>
                      </div>
                      <button type="button" onClick={() => handleRemoveCoupon(code)} className="text-red-400 hover:text-red-600 text-xs font-medium">הסר</button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <input type="text" value={couponCode} onChange={(e) => { setCouponCode(e.target.value); setCouponError(''); }} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleApplyCoupon(); } }} placeholder={appliedCoupons.length > 0 ? 'הוסף קופון נוסף' : 'הכנס קוד קופון'} className="flex-1 h-10 px-4 border-2 border-stone-200 rounded-xl text-sm focus:border-[#D97706] focus:outline-none transition-colors" style={{ fontFamily: 'Heebo, sans-serif' }} />
                <button type="button" onClick={handleApplyCoupon} className="h-10 px-5 bg-[#2C3E2D] text-white rounded-xl font-bold text-sm hover:bg-[#1a2a1b] transition-colors" style={{ fontFamily: 'Heebo, sans-serif' }}>הפעל</button>
              </div>
              {couponError && <p className="text-red-500 text-xs mt-1">{couponError}</p>}
            </div>

            {/* Personal Details Form */}
            <div className="space-y-4">
              {/* Name & Phone Section */}
              <div className="bg-white rounded-2xl p-5 md:p-6 border-2 border-stone-200 shadow-sm" data-testid="personal-details-section">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-full bg-[#D97706]/10 flex items-center justify-center">
                  <User className="w-4 h-4 text-[#D97706]" />
                </div>
                <h2 className="text-lg font-bold text-[#2C3E2D]" style={{ fontFamily: 'Heebo, sans-serif' }}>
                  פרטים אישיים
                </h2>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1.5" style={{ fontFamily: 'Heebo, sans-serif' }}>
                      שם מלא *
                    </label>
                    <input
                      type="text"
                      name="customer_name"
                      data-testid="customer-name-input"
                      value={formData.customer_name}
                      onChange={handleChange}
                      required
                      className="w-full h-11 px-4 bg-white border border-stone-200 focus:border-[#D97706] focus:ring-2 focus:ring-[#D97706]/20 rounded-lg outline-none transition-all text-sm"
                      placeholder="יוסי כהן"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1.5" style={{ fontFamily: 'Heebo, sans-serif' }}>
                      טלפון *
                    </label>
                    <input
                      type="tel"
                      name="customer_phone"
                      data-testid="customer-phone-input"
                      value={formData.customer_phone}
                      onChange={handleChange}
                      required
                      className="w-full h-11 px-4 bg-white border border-stone-200 focus:border-[#D97706] focus:ring-2 focus:ring-[#D97706]/20 rounded-lg outline-none transition-all text-sm"
                      placeholder="050-1234567"
                    />
                  </div>
                </div>
              </div>
                
              {/* Address Section */}
              {shippingMethod !== 'self_pickup' && (
                <div className="bg-white rounded-2xl p-5 md:p-6 border-2 border-stone-200 shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 rounded-full bg-[#D97706]/10 flex items-center justify-center">
                      <MapPin className="w-4 h-4 text-[#D97706]" />
                    </div>
                    <p className="text-lg font-bold text-[#2C3E2D]" style={{ fontFamily: 'Heebo, sans-serif' }}>
                      {shippingMethod === 'delivery' ? 'כתובת למשלוח *' : shippingMethod === 'pickup_zigzag' ? 'כתובת לאיתור נקודת זיגזג *' : 'כתובת'}
                    </p>
                  </div>
                  <div className="space-y-4">
                    
                    <div>
                      <label className="block text-xs text-stone-500 mb-1">רחוב</label>
                      <input
                        type="text"
                        name="street"
                        data-testid="street-input"
                        value={formData.street}
                        onChange={handleChange}
                        required={shippingMethod === 'delivery' || shippingMethod === 'pickup_zigzag'}
                        className="w-full h-11 px-4 bg-white border border-stone-200 focus:border-[#D97706] focus:ring-2 focus:ring-[#D97706]/20 rounded-lg outline-none transition-all text-sm"
                        placeholder="הרצל"
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-stone-500 mb-1">מספר</label>
                        <input
                          type="text"
                          name="house_number"
                          data-testid="house-number-input"
                          value={formData.house_number}
                          onChange={handleChange}
                          required={shippingMethod === 'delivery' || shippingMethod === 'pickup_zigzag'}
                          className="w-full h-11 px-4 bg-white border border-stone-200 focus:border-[#D97706] focus:ring-2 focus:ring-[#D97706]/20 rounded-lg outline-none transition-all text-sm"
                          placeholder="12"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-xs text-stone-500 mb-1">עיר</label>
                        <input
                          type="text"
                          name="city"
                          data-testid="city-input"
                          value={formData.city}
                          onChange={handleChange}
                          required={shippingMethod === 'delivery' || shippingMethod === 'pickup_zigzag'}
                          className="w-full h-11 px-4 bg-white border border-stone-200 focus:border-[#D97706] focus:ring-2 focus:ring-[#D97706]/20 rounded-lg outline-none transition-all text-sm"
                          placeholder="תל אביב"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
                
              {/* Notes Section */}
              <div className="bg-white rounded-2xl p-5 md:p-6 border-2 border-stone-200 shadow-sm">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1.5" style={{ fontFamily: 'Heebo, sans-serif' }}>
                    הערות
                  </label>
                  <textarea
                    name="notes"
                    data-testid="order-notes-input"
                    value={formData.notes}
                    onChange={handleChange}
                    rows="2"
                    className="w-full px-4 py-2.5 bg-white border-2 border-stone-200 focus:border-[#D97706] focus:ring-2 focus:ring-[#D97706]/20 rounded-lg outline-none transition-all resize-none text-sm"
                    placeholder="הערות נוספות להזמנה"
                  />
                </div>
              </div>
            </div>
          </div>
          
          {/* Right Column: Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-6 md:p-8 border border-stone-100 sticky top-24" data-testid="order-summary">
              <h2 className="text-2xl font-bold text-[#2C3E2D] mb-6" style={{ fontFamily: 'Heebo, sans-serif' }}>
                סיכום הזמנה
              </h2>
              
              <div className="space-y-4 mb-6">
                <div className="flex justify-between text-stone-600">
                  <span>סה"כ מוצרים:</span>
                  <span data-testid="subtotal">₪{getCartTotal().toFixed(2)}</span>
                </div>
                
                {/* Coupon Code */}
                <div className="border-t border-stone-200 pt-4 hidden lg:block">
                  <div className="flex items-center gap-2 mb-2">
                    <Tag className="w-4 h-4 text-[#D97706]" />
                    <span className="font-semibold text-[#2C3E2D] text-sm" style={{ fontFamily: 'Heebo, sans-serif' }}>קוד קופון</span>
                  </div>
                  {appliedCoupons.length > 0 && (
                    <div className="space-y-2 mb-3">
                      {appliedCoupons.map((code) => (
                        <div key={code} className="flex items-center justify-between bg-green-50 border border-green-200 rounded-xl p-2.5" data-testid={`applied-coupon-${code}`}>
                          <div className="flex items-center gap-2">
                            <span className="text-green-700 font-bold text-sm">קופון {code}</span>
                            <span className="text-green-600 text-sm">(-₪{VALID_COUPONS[code]})</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveCoupon(code)}
                            data-testid={`remove-coupon-${code}`}
                            className="text-red-400 hover:text-red-600 text-xs font-medium"
                          >
                            הסר
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={couponCode}
                        onChange={(e) => { setCouponCode(e.target.value); setCouponError(''); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleApplyCoupon(); } }}
                        placeholder={appliedCoupons.length > 0 ? 'הוסף קופון נוסף' : 'הכנס קוד קופון'}
                        data-testid="coupon-input"
                        className="flex-1 h-10 px-4 border-2 border-stone-200 rounded-xl text-sm focus:border-[#D97706] focus:outline-none transition-colors"
                        style={{ fontFamily: 'Heebo, sans-serif' }}
                      />
                      <button
                        type="button"
                        onClick={handleApplyCoupon}
                        data-testid="apply-coupon-button"
                        className="h-10 px-5 bg-[#2C3E2D] text-white rounded-xl font-bold text-sm hover:bg-[#1a2a1b] transition-colors"
                        style={{ fontFamily: 'Heebo, sans-serif' }}
                      >
                        הפעל
                      </button>
                    </div>
                    {couponError && (
                      <p className="text-red-500 text-xs mt-1" data-testid="coupon-error">{couponError}</p>
                    )}
                  </div>
                </div>

                {appliedCoupons.length > 0 && (
                  <div className="flex justify-between text-green-600 font-medium">
                    <span>הנחת קופונים ({appliedCoupons.length}):</span>
                    <span data-testid="discount-amount">-₪{discount.toFixed(2)}</span>
                  </div>
                )}

                {/* Payment Method Selection */}
                <div className="border-t border-stone-200 pt-4">
                  <p className="text-sm font-semibold text-[#2C3E2D] mb-3" style={{ fontFamily: 'Heebo, sans-serif' }}>
                    אמצעי תשלום *
                  </p>
                  <div className="space-y-2" data-testid="payment-methods">
                    {[
                      { value: 'credit_card', label: 'כרטיס אשראי', icon: <CreditCard className="w-5 h-5 text-blue-500" />, recommended: true },
                      { value: 'bit', label: 'ביט', icon: <img src="https://customer-assets.emergentagent.com/job_d56f6086-1f6c-43f9-a07d-5d56a502df97/artifacts/6m20ykko_image.png" alt="bit" className="w-6 h-6 rounded-md" /> },
                      { value: 'paypal', label: 'PayPal', icon: <span className="w-6 h-6 rounded bg-[#003087] text-white flex items-center justify-center text-[9px] font-black">PP</span> },
                      { value: 'paybox', label: 'פייבוקס', icon: <span className="w-6 h-6 rounded bg-[#6366f1] text-white flex items-center justify-center text-[8px] font-black">PB</span> },
                      { value: 'bank_transfer', label: 'העברה בנקאית', icon: <Landmark className="w-5 h-5 text-emerald-600" /> },
                      { value: 'cash', label: 'מזומן בעת מסירה', icon: <Banknote className="w-5 h-5 text-green-600" /> }
                    ].map((method) => (
                      <label
                        key={method.value}
                        data-testid={`payment-method-${method.value}`}
                        className={`flex items-center gap-3 p-3 border-2 rounded-xl cursor-pointer transition-all ${
                          paymentMethod === method.value
                            ? 'border-[#D97706] bg-[#D97706]/5'
                            : 'border-stone-200 hover:border-stone-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="payment_method"
                          value={method.value}
                          checked={paymentMethod === method.value}
                          onChange={(e) => setPaymentMethod(e.target.value)}
                          className="w-4 h-4 text-[#D97706] focus:ring-[#D97706]"
                        />
                        {method.icon}
                        <span className="font-medium flex-1 text-sm" style={{ fontFamily: 'Heebo, sans-serif' }}>
                          {method.label}
                        </span>
                        {method.recommended && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                            מומלץ
                          </span>
                        )}
                      </label>
                    ))}
                  </div>
                  {paymentMethod === 'bank_transfer' && (
                    <p className="text-xs text-stone-500 mt-2 text-center" style={{ fontFamily: 'Heebo, sans-serif' }}>
                      פרטי הבנק תקבל לאחר אישור ההזמנה מטה
                    </p>
                  )}
                </div>
                
                {/* Shipping Options */}
                <div className="border-t border-stone-200 pt-4">
                  <p className="text-sm font-semibold text-[#2C3E2D] mb-3" style={{ fontFamily: 'Heebo, sans-serif' }}>
                    בחר אפשרות משלוח:
                  </p>
                  
                  <div className="space-y-2">
                    {/* Pickup Point Option - hidden when cash is selected */}
                    {paymentMethod !== 'cash' && (
                      <div
                        data-testid="shipping-pickup"
                        className={`border-2 rounded-xl transition-all ${
                          shippingMethod === 'pickup' 
                            ? 'border-[#D97706] bg-[#D97706]/5' 
                            : 'border-stone-200 hover:border-stone-300'
                        }`}
                      >
                        <label
                          className="flex items-start gap-3 p-3 cursor-pointer"
                          onClick={(e) => { e.preventDefault(); setShippingMethod('pickup'); setPickupProvider(null); setSelectedPickupPoint(null); }}
                        >
                          <input type="radio" name="shipping" value="pickup" checked={shippingMethod === 'pickup'} readOnly className="mt-1 w-4 h-4 text-[#D97706] focus:ring-[#D97706]" />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <MapPin className="w-4 h-4 text-[#D97706]" />
                              <span className="font-semibold text-[#2C3E2D] text-sm" style={{ fontFamily: 'Heebo, sans-serif' }}>נקודת איסוף</span>
                              {isFreeShipping ? (
                                <span className="mr-auto text-sm"><span className="line-through text-stone-400">₪17</span> <span className="text-green-600 font-bold">חינם</span></span>
                              ) : (
                                <span className="text-[#D97706] font-bold mr-auto text-sm">₪17</span>
                              )}
                            </div>
                            {selectedPickupPoint && (
                              <p className="text-xs text-green-600 font-bold">
                                {selectedPickupPoint.PointName}
                                {/* Customer-facing label intentionally hides
                                    the underlying provider (UPS / HFD) -
                                    they don't need to know. */}
                              </p>
                            )}
                          </div>
                        </label>
                        
                        {/* Pickup point selection - unified map */}
                        {shippingMethod === 'pickup' && !selectedPickupPoint && (
                          <div className="px-3 pb-3">
                            <button
                              type="button"
                              onClick={handlePickupSelect}
                              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-[#D97706] text-white text-sm font-bold hover:bg-[#B45309] transition-colors"
                              data-testid="open-pickup-map-btn"
                            >
                              <MapPin className="w-4 h-4" /> פתח מפת נקודות איסוף
                            </button>
                            <p className="text-[10px] text-stone-400 text-center mt-1">
                              <span className="inline-block w-2 h-2 rounded-full bg-[#2563EB] ml-1"></span>נקודות איסוף ארצי
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* HFD list-picker option — search by city, no map */}
                    {paymentMethod !== 'cash' && (
                      <div
                        data-testid="shipping-pickup-hfd"
                        className={`border-2 rounded-xl transition-all ${
                          shippingMethod === 'pickup_hfd'
                            ? 'border-[#D97706] bg-[#D97706]/5'
                            : 'border-stone-200 hover:border-stone-300'
                        }`}
                      >
                        <label
                          className="flex items-start gap-3 p-3 cursor-pointer"
                          onClick={(e) => { e.preventDefault(); setShippingMethod('pickup_hfd'); setSelectedPickupPoint(null); setPickupProvider(null); }}
                        >
                          <input type="radio" name="shipping" value="pickup_hfd" checked={shippingMethod === 'pickup_hfd'} readOnly className="mt-1 w-4 h-4 text-[#D97706] focus:ring-[#D97706]" />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <MapPin className="w-4 h-4 text-[#D97706]" />
                              <span className="font-semibold text-[#2C3E2D] text-sm" style={{ fontFamily: 'Heebo, sans-serif' }}>נקודת איסוף HFD</span>
                              {isFreeShipping ? (
                                <span className="mr-auto text-sm"><span className="line-through text-stone-400">₪17</span> <span className="text-green-600 font-bold">חינם</span></span>
                              ) : (
                                <span className="text-[#D97706] font-bold mr-auto text-sm">₪17</span>
                              )}
                            </div>
                            <p className="text-xs text-stone-500 leading-tight">(חיפוש נקודה לפי עיר ובחירה מרשימה)</p>
                            {shippingMethod === 'pickup_hfd' && hfdListPoint && (
                              <p className="text-xs text-green-600 font-bold mt-1" data-testid="cart-hfd-selected">
                                {hfdListPoint.spotName}, {hfdListPoint.city}
                                <button type="button" onClick={(e) => { e.stopPropagation(); setHfdListPoint(null); }} className="text-[#D97706] underline mr-2" data-testid="cart-hfd-change">החלף</button>
                              </p>
                            )}
                          </div>
                        </label>
                        {shippingMethod === 'pickup_hfd' && !hfdListPoint && (
                          <div className="px-3 pb-3 space-y-2">
                            <input
                              value={hfdListSearch}
                              onChange={(e) => setHfdListSearch(e.target.value)}
                              placeholder="חפש לפי עיר / שם נקודה..."
                              className="w-full border border-stone-300 focus:border-[#D97706] rounded-lg px-3 py-2 text-sm outline-none"
                              data-testid="cart-hfd-search"
                            />
                            {hfdListLoading && <p className="text-xs text-stone-400 text-center py-1">טוען נקודות איסוף...</p>}
                            {!hfdListLoading && hfdListSearch.trim().length >= 2 && (() => {
                              const q = hfdListSearch.trim();
                              const matches = hfdAllPoints.filter(pt =>
                                (pt.city || '').includes(q) || (pt.spotName || '').includes(q) || `${pt.street || ''}`.includes(q)
                              ).slice(0, 20);
                              if (matches.length === 0) return <p className="text-xs text-stone-400 text-center py-1">לא נמצאו נקודות — נסה עיר אחרת</p>;
                              return (
                                <div className="max-h-48 overflow-y-auto space-y-1" data-testid="cart-hfd-results">
                                  {matches.map(pt => (
                                    <button
                                      type="button"
                                      key={pt.spotId}
                                      onClick={() => { setHfdListPoint(pt); setHfdListSearch(''); }}
                                      className="w-full text-right bg-stone-50 hover:bg-[#D97706]/10 border border-stone-200 hover:border-[#D97706]/50 rounded-lg px-3 py-2 transition-all"
                                      data-testid={`cart-hfd-point-${pt.spotId}`}
                                    >
                                      <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ml-2 ${pt.kind === 'locker' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>{pt.kind === 'locker' ? 'לוקר' : 'חנות'}</span>
                                      <span className="text-sm font-bold text-[#2C3E2D]">{pt.spotName}</span>
                                      <span className="block text-[11px] text-stone-500">{[pt.street, pt.house].filter(Boolean).join(' ') || ''}{pt.street ? ', ' : ''}{pt.city}</span>
                                    </button>
                                  ))}
                                </div>
                              );
                            })()}
                            {!hfdListLoading && hfdListSearch.trim().length < 2 && hfdAllPoints.length > 0 && (
                              <p className="text-[10px] text-stone-400 text-center">הקלד לפחות 2 תווים לחיפוש</p>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Zigzag Pickup Option - auto-locates closest locker by
                        the customer's address (eHouse handles the routing on
                        their end). No upfront map selection. Hidden when cash
                        is selected (Zigzag does not support COD). */}
                    {paymentMethod !== 'cash' && (
                      <label
                        data-testid="shipping-pickup-zigzag"
                        className={`flex items-start gap-3 p-3 border-2 rounded-xl cursor-pointer transition-all ${
                          shippingMethod === 'pickup_zigzag'
                            ? 'border-[#D97706] bg-[#D97706]/5'
                            : 'border-stone-200 hover:border-stone-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="shipping"
                          value="pickup_zigzag"
                          checked={shippingMethod === 'pickup_zigzag'}
                          onChange={(e) => { setShippingMethod(e.target.value); setSelectedPickupPoint(null); setPickupProvider(null); }}
                          className="mt-1 w-4 h-4 text-[#D97706] focus:ring-[#D97706]"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <MapPin className="w-4 h-4 text-[#D97706]" />
                            <span className="font-semibold text-[#2C3E2D] text-sm" style={{ fontFamily: 'Heebo, sans-serif' }}>נקודת איסוף זיגזג</span>
                            {isFreeShipping ? (
                              <span className="mr-auto text-sm"><span className="line-through text-stone-400">₪17</span> <span className="text-green-600 font-bold">חינם</span></span>
                            ) : (
                              <span className="text-[#D97706] font-bold mr-auto text-sm">₪17</span>
                            )}
                          </div>
                          <p className="text-xs text-stone-500 leading-tight">
                            (מאתר אוטומטית את נקודת האיסוף הקרובה לכתובת שציינתם)
                          </p>
                        </div>
                      </label>
                    )}
                    
                    {/* Self Pickup Option - hidden when cash is selected */}
                    {paymentMethod !== 'cash' && (
                      <label
                        data-testid="shipping-self-pickup"
                        className={`flex items-start gap-3 p-3 border-2 rounded-xl cursor-pointer transition-all ${
                          shippingMethod === 'self_pickup' 
                            ? 'border-[#D97706] bg-[#D97706]/5' 
                            : 'border-stone-200 hover:border-stone-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="shipping"
                          value="self_pickup"
                          checked={shippingMethod === 'self_pickup'}
                          onChange={(e) => setShippingMethod(e.target.value)}
                          className="mt-1 w-4 h-4 text-[#D97706] focus:ring-[#D97706]"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Store className="w-4 h-4 text-[#D97706]" />
                            <span className="font-semibold text-[#2C3E2D] text-sm" style={{ fontFamily: 'Heebo, sans-serif' }}>
                              איסוף עצמי
                            </span>
                            <span className="text-green-600 font-bold mr-auto text-sm">חינם</span>
                          </div>
                          <p className="text-xs text-stone-500">
                            ראשון לציון, דרך המכבים 58
                          </p>
                        </div>
                      </label>
                    )}
                    
                    {/* Home Delivery Option */}
                    <label
                      data-testid="shipping-delivery"
                      className={`flex items-start gap-3 p-3 border-2 rounded-xl cursor-pointer transition-all ${
                        shippingMethod === 'delivery' 
                          ? 'border-[#D97706] bg-[#D97706]/5' 
                          : 'border-stone-200 hover:border-stone-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="shipping"
                        value="delivery"
                        checked={shippingMethod === 'delivery'}
                        onChange={(e) => setShippingMethod(e.target.value)}
                        className="mt-1 w-4 h-4 text-[#D97706] focus:ring-[#D97706]"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Truck className="w-4 h-4 text-[#D97706]" />
                          <span className="font-semibold text-[#2C3E2D] text-sm" style={{ fontFamily: 'Heebo, sans-serif' }}>
                            משלוח עד הבית
                          </span>
                          {isFreeShipping ? (
                            <span className="mr-auto text-sm"><span className="line-through text-stone-400">₪29</span> <span className="text-green-600 font-bold">חינם</span></span>
                          ) : (
                            <span className="text-[#D97706] font-bold mr-auto text-sm">₪29</span>
                          )}
                        </div>
                      </div>
                    </label>
                  </div>
                </div>
                
                <div className="flex justify-between text-stone-600">
                  <span>משלוח:</span>
                  <span data-testid="shipping-cost">{shippingCost === 0 && shippingMethod !== 'self_pickup' ? <span className="text-green-600 font-bold">חינם</span> : shippingCost === 0 ? <span className="text-green-600 font-bold">חינם</span> : `₪${shippingCost.toFixed(2)}`}</span>
                </div>
                {isFreeShipping && shippingMethod !== 'self_pickup' && (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-center">
                    <p className="text-green-700 text-sm font-bold">העגלה מעל ₪{effectiveThreshold} - לכן המשלוח ללא עלות :)</p>
                    <p className="text-green-600 text-xs mt-1">חסכת <strong>₪{baseShippingCost}</strong> על המשלוח!</p>
                  </div>
                )}
                
                <div className="border-t border-stone-200 pt-4">
                  <div className="flex justify-between text-xl font-bold text-[#2C3E2D]" style={{ fontFamily: 'Heebo, sans-serif' }}>
                    <span>סך הכל:</span>
                    <span data-testid="total">₪{totalWithShipping.toFixed(2)}</span>
                  </div>
                </div>
              </div>
              
              <button
                type="submit"
                data-testid="checkout-button"
                disabled={loading}
                className="w-full h-14 rounded-full bg-[#D97706] text-white font-bold text-lg hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mb-4"
                style={{ fontFamily: 'Heebo, sans-serif' }}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    מעבד...
                  </>
                ) : paymentMethod === 'credit_card' ? (
                  <>
                    המשך לתשלום מאובטח
                    <CreditCard className="w-5 h-5" />
                  </>
                ) : (
                  'אשר הזמנה'
                )}
              </button>

              <p className="text-center text-[11px] text-stone-500 mb-3 px-2 leading-relaxed" style={{ fontFamily: 'Heebo, sans-serif' }} data-testid="terms-disclaimer">
                בלחיצה על המשך תשלום, קראתי והנני מאשר את{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-[#D97706] hover:underline font-bold">
                  תנאי השימוש ותקנון האתר
                </a>
              </p>

              {paymentMethod === 'bank_transfer' && (
                <p className="text-center text-sm text-stone-500 mb-2" style={{ fontFamily: 'Heebo, sans-serif' }}>
                  לאחר אישור תועבר לפרטי הבנק
                </p>
              )}

              {paymentMethod === 'credit_card' && (
                <div className="flex flex-col items-center gap-3 mt-2">
                  <img
                    src="https://customer-assets.emergentagent.com/job_d56f6086-1f6c-43f9-a07d-5d56a502df97/artifacts/vhi6op6d_images.png"
                    alt="תשלום מאובטח"
                    className="h-16 object-contain"
                    data-testid="cart-secure-payment-badge"
                  />
                </div>
              )}
              
              <button
                type="button"
                onClick={clearCart}
                data-testid="clear-cart-button"
                className="w-full h-10 text-stone-600 hover:text-red-500 transition-colors font-medium mt-2"
              >
                רוקן עגלה
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default Cart;
